<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
    overlay-opacity="0.85"
    content-class="modern-agency-dialog"
  >
    <v-card class="rounded-xl" elevation="16">
      <!-- Header with gradient background -->
      <v-card-title
        class="editor-header d-flex justify-space-between align-center px-6 py-4"
      >
        <div class="text-h5 font-weight-bold white--text">
          <v-icon large color="white" class="mr-3">mdi-account-circle</v-icon>
          Complete Profile
        </div>
        <v-btn
          icon
          @click="onClose"
          class="close-btn"
          color="white"
          depressed
          large
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Help text -->
      <v-card-text class="px-6 pt-5 pb-1 text-body-1 text--secondary">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
          Just add your profile and a few words about you
        </div>
      </v-card-text>

      <!-- Error/Success message with smooth transition -->
      <v-expand-transition>
        <v-card-text class="px-6 py-0" v-if="message">
          <v-alert
            type="error"
            class="mb-4 elevation-2"
            transition="scale-transition"
            icon="mdi-alert-circle"
          >
            {{ message }}
          </v-alert>
        </v-card-text>
      </v-expand-transition>

      <!-- Main content -->
      <v-card-text class="px-6 py-2">
        <v-row>
          <v-col cols="12" class="d-flex flex-column align-center py-4">
            <div class="text-subtitle-1 font-weight-medium primary--text mb-3">
              Profile Picture
            </div>
            <editable-avatar
              :image="image"
              :onImageSelected="onImageSelected"
              size="140"
              class="elevation-4 rounded-circle"
            />
          </v-col>

          <v-col cols="12" class="py-2">
            <v-textarea
              v-model="about_me"
              label="About Me*"
              outlined
              dense
              rows="4"
              auto-grow
              prepend-inner-icon="mdi-text"
              :rules="[rules.required]"
              @input="onChange"
              class="modern-input"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Action buttons -->
      <v-card-actions class="px-6 pb-6 pt-4 d-flex justify-center">
        <v-btn
          color="primary"
          @click="onSave"
          depressed
          width="200"
          :loading="loading"
          class="px-6 save-btn"
          large
          dark
        >
          <template v-slot:loader>
            <span class="custom-loader">
              <v-icon light>mdi-autorenew</v-icon>
            </span>
          </template>
          <v-icon left>mdi-check</v-icon>
          Continue
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import EditableAvatar from "@/components/EditableAvatar.vue";

export default {
  components: { EditableAvatar },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    onClose: {
      type: Function
    }
  },
  data() {
    return {
      about_me: "",
      image: "",
      message: null,
      loading: false,
      rules: {
        required: value => !!value || "This field is required"
      },
      selectedImage: null
    };
  },
  methods: {
    ...mapActions("auth", {
      completeProfile: "completeProfile"
    }),
    onChange() {
      this.message = null;
    },
    onSave() {
      if (!this.about_me) {
        this.message = "Please add a description about yourself";
        return;
      }

      const formData = new FormData();
      if (this.selectedImage) {
        formData.append("image", this.selectedImage);
      }
      formData.append("about_me", this.about_me);

      this.loading = true;
      this.completeProfile(formData)
        .then(() => {
          this.onClose();
        })
        .catch(error => {
          this.message = error.response?.data?.message || "An error occurred";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onImageSelected(image) {
      this.selectedImage = image;
      this.message = null;
    }
  },
  computed: {
    ...mapState("auth", {
      profile: "profile"
    })
  },
  mounted() {
    this.about_me = this.profile.about_me;
    this.image = this.profile.midThumbnail
      ? this.profile.midThumbnail
      : this.profile.image;
  }
};
</script>

<style scoped>
.modern-agency-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.editor-header {
    background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
}

.close-btn {
  transition: all 0.3s ease;
}

.close-btn:hover {
  transform: rotate(90deg);
  background: rgba(255, 255, 255, 0.2) !important;
}

.modern-input::v-deep .v-input__slot {
  border-radius: 10px !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  background-color: white !important;
}

.save-btn {
  box-shadow: 0 4px 12px rgba(58, 123, 213, 0.3) !important;
  transition: all 0.3s ease;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(58, 123, 213, 0.4) !important;
}

.save-btn:active {
  transform: translateY(0);
}

.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Scrollbar styling */
.modern-agency-dialog::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.modern-agency-dialog::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.modern-agency-dialog::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  border-radius: 10px;
}

.modern-agency-dialog::-webkit-scrollbar-thumb:hover {
  background: #3a7bd5;
}
</style>